<script lang="ts">
  import { onMount } from 'svelte';
  import { replace, push, pop } from 'svelte-spa-router';
  import { session } from '../lib/session';
  import { adminMode } from '../lib/admin-mode';
  import { api, ApiError } from '../lib/api';
  import { formatDateTime } from '../lib/format';
  import type { AdminPaymentDetailDTO, PaymentStatus } from '../lib/types';

  interface Props {
    params?: { id?: string };
  }
  let { params }: Props = $props();
  const id = $derived(params?.id ?? '');

  let payment = $state<AdminPaymentDetailDTO | null>(null);
  let loading = $state(true);
  let notFound = $state(false);
  let error = $state<string | null>(null);

  const STATUS_LABEL: Record<PaymentStatus, string> = {
    succeeded: 'Успішно',
    refunded: 'Повернення',
    failed: 'Відхилено',
    pending: 'Очікує',
  };
  const STATUS_CLASS: Record<PaymentStatus, string> = {
    succeeded: 'badge-active',
    refunded: 'badge-expired',
    failed: 'badge-expired',
    pending: 'badge-muted',
  };
  const SOURCE_LABEL: Record<string, string> = {
    webhook: 'Вебхук (SendPulse)',
    manual: 'Вручну',
    api: 'API',
  };

  function uah(cents: number): string {
    return `₴${(cents / 100).toLocaleString('uk-UA', { maximumFractionDigits: 2 })}`;
  }

  // Back to wherever we came from (dashboard drill-in / payments list / card);
  // fall back to the payments list if opened fresh with no in-app history.
  function goBack(): void {
    if (window.history.length > 1) pop();
    else replace('/admin/payments');
  }

  onMount(async () => {
    if (!$session) {
      replace('/');
      return;
    }
    if (!$session.isAdmin) {
      replace('/home');
      return;
    }
    adminMode.set('admin');
    if (!id) {
      notFound = true;
      loading = false;
      return;
    }
    try {
      payment = await api.adminPayment(id);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) notFound = true;
      else error = 'Не вдалось завантажити оплату';
    } finally {
      loading = false;
    }
  });
</script>

<div class="screen">
  <div class="admin-header">
    <button type="button" class="back-link" onclick={goBack}>← Назад</button>
    <div class="header-title">Оплата</div>
    <span class="header-spacer"></span>
  </div>

  <div class="scroll">
    {#if loading}
      <div class="placeholder">Завантажуємо…</div>
    {:else if notFound}
      <div class="placeholder">Оплату не знайдено</div>
    {:else if error}
      <div class="placeholder err">{error}</div>
    {:else if payment}
      {@const p = payment}
      <div class="amount-head">
        <div class="amount" class:refund={p.status === 'refunded'}>
          {p.status === 'refunded' ? '−' : ''}{uah(p.amountCents)} {p.currency}
        </div>
        <span class="badge {STATUS_CLASS[p.status]}">{STATUS_LABEL[p.status]}</span>
      </div>

      <div class="info-card">
        <div class="row">
          <div class="k">Дата й час</div>
          <div class="v">{formatDateTime(p.occurredAt)}</div>
        </div>
        <div class="row">
          <div class="k">Продукт</div>
          <div class="v">
            {#if p.product}
              <a href={`#/admin/product/${p.product.id}/edit`} class="link">{p.product.title}</a>
            {:else if p.rawProduct}
              <span>{p.rawProduct}</span> <span class="tag-unmatched">не зіставлено</span>
            {:else}
              —
            {/if}
          </div>
        </div>
        {#if p.rawProduct && p.product}
          <div class="row">
            <div class="k">Назва з оплати</div>
            <div class="v">{p.rawProduct}</div>
          </div>
        {/if}
      </div>

      <div class="sec">Клієнт</div>
      <div class="info-card">
        <div class="row">
          <div class="k">Імʼя</div>
          <div class="v">{p.userName || p.payerName || '—'}</div>
        </div>
        <div class="row">
          <div class="k">Контакт</div>
          <div class="v">
            {#if p.telegramId}
              <button type="button" class="link" onclick={() => push(`/admin/user/${p.telegramId}`)}>
                {p.telegramId.startsWith('-')
                  ? 'Контакт з оплати'
                  : p.userUsername
                    ? `@${p.userUsername}`
                    : `ID ${p.telegramId}`}
              </button>
            {:else}
              —
            {/if}
          </div>
        </div>
        <div class="row">
          <div class="k">Telegram ID</div>
          <div class="v">{p.telegramId && !p.telegramId.startsWith('-') ? p.telegramId : '—'}</div>
        </div>
        <div class="row">
          <div class="k">Email</div>
          <div class="v">{p.payerEmail || '—'}</div>
        </div>
        <div class="row">
          <div class="k">Телефон</div>
          <div class="v">{p.payerPhone || '—'}</div>
        </div>
      </div>

      <div class="sec">Деталі платежу</div>
      <div class="info-card">
        <div class="row">
          <div class="k">№ замовлення</div>
          <div class="v">{p.externalRef || '—'}</div>
        </div>
        <div class="row">
          <div class="k">Спосіб оплати</div>
          <div class="v">{p.paymentMethod || '—'}{p.cardLast4 ? ` ····${p.cardLast4}` : ''}</div>
        </div>
        <div class="row">
          <div class="k">Провайдер</div>
          <div class="v">{p.provider || '—'}</div>
        </div>
        <div class="row">
          <div class="k">Статус (raw)</div>
          <div class="v">{p.rawStatus || '—'}</div>
        </div>
        <div class="row">
          <div class="k">Джерело</div>
          <div class="v">{SOURCE_LABEL[p.importedFrom] ?? p.importedFrom}</div>
        </div>
        <div class="row">
          <div class="k">Воронка / джерело</div>
          <div class="v">{p.source || '—'}</div>
        </div>
        <div class="row">
          <div class="k">Додано в систему</div>
          <div class="v">{formatDateTime(p.createdAt)}</div>
        </div>
      </div>

      <!-- UTM — окрема вісь від воронки вище: звідки прийшла ЛЮДИНА, а не оплата. -->
      <div class="sec">UTM-мітки оплати</div>
      <div class="info-card">
        {#if p.utmSource || p.utmMedium || p.utmCampaign || p.utmContent || p.utmTerm}
          <div class="row">
            <div class="k">utm_source</div>
            <div class="v">{p.utmSource || '—'}</div>
          </div>
          <div class="row">
            <div class="k">utm_medium</div>
            <div class="v">{p.utmMedium || '—'}</div>
          </div>
          <div class="row">
            <div class="k">utm_campaign</div>
            <div class="v">{p.utmCampaign || '—'}</div>
          </div>
          <div class="row">
            <div class="k">utm_content</div>
            <div class="v">{p.utmContent || '—'}</div>
          </div>
          <div class="row">
            <div class="k">utm_term</div>
            <div class="v">{p.utmTerm || '—'}</div>
          </div>
        {:else}
          <div class="row">
            <div class="v utm-empty">Міток немає — оплата прийшла без UTM.</div>
          </div>
        {/if}
      </div>
    {/if}
  </div>
</div>

<style>
  .screen { display: flex; flex-direction: column; flex: 1; min-height: 0; background: var(--bg); }
  .admin-header {
    background: linear-gradient(135deg, var(--dark), var(--dark3));
    padding: 14px 18px; color: var(--text-light); display: flex; align-items: center; gap: 10px; flex-shrink: 0;
  }
  .back-link { font-size: 12px; color: rgba(251, 246, 236, 0.9); text-decoration: none; background: none; border: none; cursor: pointer; font-family: inherit; padding: 0; }
  .header-title { flex: 1; text-align: center; font-size: 14px; font-weight: 600; }
  .header-spacer { width: 40px; }
  .scroll { flex: 1; overflow-y: auto; padding: 18px 16px 100px; }

  .amount-head {
    display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 18px;
  }
  .amount { font-size: 26px; font-weight: 700; color: var(--text); }
  .amount.refund { color: var(--status-inactive); }
  .badge { padding: 5px 13px; border-radius: 999px; font-size: 12px; font-weight: 600; flex-shrink: 0; }
  .badge-active { background: var(--green-light, var(--green-light)); color: var(--status-active); }
  .badge-expired { background: var(--danger-bg); color: var(--status-inactive); }
  .badge-muted { background: var(--bg2); color: var(--text-muted); }

  .sec {
    font-size: 11px; font-weight: 700; color: var(--text-muted); text-transform: uppercase;
    letter-spacing: 0.5px; margin: 18px 4px 8px;
  }
  .utm-empty { color: var(--text-muted); }
  .info-card {
    background: var(--bg2); border: 1px solid var(--border); border-radius: 14px; overflow: hidden;
  }
  .row {
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
    padding: 12px 14px; border-bottom: 1px solid var(--border); font-size: 13px;
  }
  .row:last-child { border-bottom: none; }
  .k { color: var(--text-muted); font-weight: 500; flex-shrink: 0; }
  .v { color: var(--text); font-weight: 600; text-align: right; word-break: break-word; min-width: 0; }
  .link {
    color: var(--accent-text); font-weight: 600; text-decoration: none; background: none; border: none;
    padding: 0; cursor: pointer; font-family: inherit; font-size: 13px;
  }
  .tag-unmatched {
    font-size: 10px; font-weight: 600; padding: 1px 6px; border-radius: 6px;
    background: var(--danger-bg); color: var(--status-inactive);
  }
  .placeholder {
    padding: 30px 16px; text-align: center; color: var(--text-muted); font-size: 13px;
    border: 1px dashed var(--border2); border-radius: 12px; background: var(--bg2);
  }
  .placeholder.err { color: var(--status-inactive); }
</style>
